import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DateTime } from "luxon";
import CommonTextField from "../../components/commonTextField/CommonTextField";
import WelcomeCard from "../../components/WelcomeCard";
import CommonInputField from "../../components/CommonInputField/CommonInputField";
import CommonDropdown from "../../components/commonDropDown/CommonDropDown";
import TransparentButton from "../../components/transparentButton/TransparentButton";
import FlexibleTextarea from "../../components/textArea/FlexibleTextarea";

import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { timezones } from "../../constants/constants";

const schema = yup.object().shape({
  groupName: yup.string().required("Group Name is required"),
  mentor: yup.string().required("Mentor is required"),
  timezone: yup.string().required("Timezone is required"),
  frequency: yup.string().required("Frequency is required"),
  startDate: yup.string().required("Start Date is required"),
  endDate: yup.string().required("End Date is required"),
  seatsAvailable: yup
    .number()
    .typeError("Seats must be a number")
    .min(0, "Seats must be 0 or more")
    .required("Seats are required"),
  pricePerMentee: yup
    .number()
    .typeError("Price must be a number")
    .min(0, "Price must be 0 or more")
    .required("Price is required"),
  description: yup.string().required("Description is required"),
  targetUsers: yup.string().required("Target Users is required"),
  whatUserWillGetOutOfThis: yup.string().required("What user will get out of this? is required")
});

const CreateGroupMentoring = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cohort } = location.state || {};

  const [users, setUsers] = useState([]);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const response = await api1.get(API_ENDPOINTS.GET_MENTORS_LIST);
        const formatted = response.data.data
          .map((mentor) => ({
            label: `${mentor.firstName} ${mentor.lastName}`,
            value: { id: mentor._id, name: `${mentor.firstName} ${mentor.lastName}` },
          }));

        setUsers(formatted);
      } catch (error) {
        console.log("Error fetching mentors:", error);
      }
    };

    fetchMentors();
  }, []);

  useEffect(() => {
    if (cohort) {
      const timezone = cohort.timezone || DateTime.local().zoneName;;
      const start = DateTime.fromISO(cohort.startDate, { zone: "utc" }).setZone(timezone);
      const end = DateTime.fromISO(cohort.endDate, { zone: "utc" }).setZone(timezone);

      reset({
        groupName: cohort.groupName || "",
        mentor: cohort.mentor?.id || "",
        timezone: cohort.timezone || "",
        frequency: cohort.frequency || "",
        startDate: start.toFormat("yyyy-MM-dd'T'HH:mm"),
        endDate: end.toFormat("yyyy-MM-dd'T'HH:mm"),
        seatsAvailable: cohort.seatsAvailable || 0,
        pricePerMentee: cohort.pricePerMentee || 0,
        groupName: cohort.groupName || "",
        targetUsers: cohort.targetUsers || "",
        whatUserWillGetOutOfThis: cohort.whatUserWillGetOutOfThis || "",
        description: cohort.description || "",
      });
    }
  }, [cohort, reset]);

  const formattedUsers = users.map((user) => ({
    label: user.label,
    value: user.value.id,
  }));

  const handleFormSubmit = async (data) => {
    const selectedInstructor = users.find((user) => user.value.id === data.mentor);
    const payload = {
      ...data,
      mentor: selectedInstructor?.value || {},
    };

    try {
      if (cohort) {
        await api1.put(`${API_ENDPOINTS.CHORTS}/${cohort._id}`, payload);
      } else {
        await api1.post(API_ENDPOINTS.COHORTS, payload);
      }
      navigate("/dashboard/group-mentoring/list");
    } catch (error) {
      console.error("Error saving group mentoring:", error);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title="Group Mentoring" />
      <form
        className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-6"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CommonTextField
            name="groupName"
            label="Group Name"
            register={register}
            required
            errors={errors}
            placeholder="Enter Group Name"
          />

          <CommonDropdown
            label="Mentor Name"
            name="mentor"
            register={register}
            errors={errors}
            required
            options={formattedUsers}
            defaultValue={watch("mentor")}
          />

          <CommonDropdown
            label="Time zone"
            name="timezone"
            register={register}
            errors={errors}
            required
            search
            options={timezones}
            defaultValue={watch("timezone")}
          />

          <CommonDropdown
            label="Frequency"
            name="frequency"
            register={register}
            errors={errors}
            required
            search
            defaultValue={watch("frequency")}
            options={[
              { label: "Weekly", value: "weekly" },
              { label: "Bi-Weekly", value: "biweekly" },
            ]}
          />

          <CommonInputField
            name="startDate"
            label="Start Date"
            type="datetime-local"
            register={register}
            required
            errors={errors}
          />

          <CommonInputField
            name="endDate"
            label="End Date"
            type="datetime-local"
            register={register}
            required
            errors={errors}
          />

          <CommonInputField
            name="seatsAvailable"
            label="Seats"
            type="number"
            register={register}
            required
            errors={errors}
            placeholder="Enter Seats Count"
          />

          <CommonInputField
            name="pricePerMentee"
            label="Price ($)"
            type="number"
            register={register}
            required
            errors={errors}
            placeholder="Enter Price"
          />

          <CommonInputField
            label="Target Users"
            name="targetUsers"
            register={register}
            errors={errors}
            required
            placeholder="Enter Target Users"
          />

          <CommonInputField
            label="What user will get out of this?"
            name="whatUserWillGetOutOfThis"
            register={register}
            errors={errors}
            required
            placeholder="Enter what user will get out of this?"
          />
        </div>

        <FlexibleTextarea
          label="Description"
          name="description"
          register={register}
          required
          errors={errors}
          placeholder="Write a description..."
        />

        <TransparentButton
          buttonText={cohort ? "Update Group" : "Create Group"}
        />
      </form>
    </div>
  );
};

export default CreateGroupMentoring;
